import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Analizadores.scss';
import { useApiConfiguration } from '../../api/api.configuration';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import DataGrid, { Column, Button as Butt, Editing, Item as Items, Paging, Toolbar, Popup, Lookup, RequiredRule, DataGridTypes, Selection, Form, SearchPanel, Scrolling } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useAppSelector } from '../../contexts/hooks';
import { Item } from 'devextreme-react/form';
import { useScreenSize } from '../../utils/media-query';
import { ModalAnalytesByAnalyzer } from './Modals/ModalAnalytesByAnalizer';
import { SysI_Analizadores } from '../../types/types.analizadores';
import { SysIAnalitos } from '../../types/types.analitos';
import { useApiConfigUsuarios } from '../../api/api.ConfigUsuarios';

const Index = () => {
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const { getAnalizadores, newAnalizador, setAnalizador, deleteAnalizador, getAnalitos } = useApiConfiguration();
  const [data, setData] = useState<SysI_Analizadores[]>([]);
  const [analitos, setAnalitos] = useState<SysIAnalitos[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const lstSedes = useAppSelector((x) => x.configuration.Data?.ComboBox?.find((y) => y.Name === 'SysI_Sedes')?.Data);
  const [selectedAnalizer, setSelectedAnalizer] = useState({
    id: -1,
    name: '',
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const dataGridRef = useRef<DataGrid>(null);
  const Areas = useAppSelector((x) => x.configuration.Data?.Areas);
  // Referencia para evitar múltiples renderizaciones durante la selección
  const modalControlRef = useRef({
    isChangingState: false,
  });

  // Cargar analizadores al inicio
  useEffect(() => {
    fetchAnalizadores();
  }, []);

  // Cargar analitos al inicio
  useEffect(() => {
    fetchAnalitos();
  }, []);

  // Función para cargar analizadores
  const fetchAnalizadores = useCallback(() => {
    return getAnalizadores().then((res) => {
      setData(res);
      return res;
    });
  }, [getAnalizadores]);

  // Función para cargar analitos
  const fetchAnalitos = useCallback(() => {
    return getAnalitos().then((res) => {
      setAnalitos(res);
      return res;
    });
  }, [getAnalitos]);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Az_Id',
      loadMode: 'processed',
      load: () =>
        Promise.resolve({
          data: data,
          totalCount: data.length,
        }),
      insert: async (values) => {
        return newAnalizador({ Analizador: values })
          .then((res) => {
            if (res === 105) {
              return fetchAnalizadores();
            }
          })
          .catch(() => {
            throw new Error('Error insertando el analizador');
          });
      },
      update: (key, values) => {
        const currentItem = data.find((item) => item.Az_Id === key);
        if (!currentItem) {
          throw new Error('No se encontró el analizador para actualizar');
        }
        const updatedItem = { ...currentItem, ...values };
        return setAnalizador({ Analizador: updatedItem, IdAnalizador: key })
          .then((res) => {
            if (res === 107) {
              return fetchAnalizadores();
            }
          })
          .catch(() => {
            throw new Error('Error actualizando el Analizador');
          });
      },
      remove: (key) => {
        return deleteAnalizador({ Id: key }).then((res) => {
          if (res === 107) {
            return fetchAnalizadores();
          }
        });
      },
    });
  }, [data]);

  const customDataSrc = useMemo(() => {
    return new DataSource({
      store: customData,
      reshapeOnPush: true,
    });
  }, [customData]);

  const handleDetails = useCallback(
    (e) => {
      const { data }: { data: SysI_Analizadores } = e.row;
      setSelectedAnalizer({
        id: data.Az_Id,
        name: data.Az_Nombre || '',
      });
      setIsModalVisible(true);
    },
    [setSelectedAnalizer, setIsModalVisible]
  );

  const handleSelection = useCallback(({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) => {
    const dataGrid = selectedRowsData[0];
    setSelectedRow(dataGrid);
  }, []);

  const handleUpdateData = useCallback(() => {
    fetchAnalizadores();
  }, [fetchAnalizadores]);

  const closeModal = useCallback(() => {
    setIsModalVisible(false);
    setSelectedAnalizer({ id: -1, name: '' });
  }, [setIsModalVisible, setSelectedAnalizer]);

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <DataGrid className='grid' highlightChanges={false} hoverStateEnabled={true} dataSource={customDataSrc} width={'100%'} height='100%' columnAutoWidth={true} repaintChangesOnly={true} rowAlternationEnabled={true} showColumnLines={true} onSelectionChanged={handleSelection} ref={dataGridRef}>
          <SearchPanel visible={true} />
          <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
          <Editing
            mode='popup'
            allowAdding
            allowDeleting
            allowUpdating
            texts={{
              saveRowChanges: 'Guardar',
              cancelRowChanges: 'Cancelar',
              deleteRow: 'Eliminar',
              addRow: 'Agregar',
            }}>
            <Popup title='INFORMACIÓN ANALIZADOR' width={1200} height={'90%'} fullScreen={isMedium || isSmall || isXSmall} showTitle={true} />
          </Editing>
          <Paging enabled={false} />
          <Selection mode='single' />
          <Toolbar>
            <Items location='before'>
              <div className='grid-header'>Analizador</div>
            </Items>
            <Item name='searchPanel' />
            <Item name='addRowButton' />
            <Items location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='RefrecarResult' icon='refresh' text='Refresh' stylingMode='text' onClick={handleUpdateData} />
              <Tooltip target={'#RefrecarResult'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Refrescar datos
              </Tooltip>
            </Items>
          </Toolbar>
          <Column caption='ID' allowEditing={false} dataField='Az_Id' alignment='center' width={50} />
          <Column caption='AREA' allowEditing={true} dataField='Az_Aa_Id' alignment='center' width={120}>
            <Lookup dataSource={Areas} valueExpr='Aa_Id' displayExpr='Aa_Nombre' />
            <RequiredRule />
          </Column>
          <Column caption='ANALIZADOR' allowEditing={true} dataField='Az_Nombre' alignment='left' minWidth={200}>
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='SEDE' visible={true} allowEditing={true} dataField='Az_Sd_Id' width={150}>
            <Lookup dataSource={lstSedes} valueExpr='Sd_Id' displayExpr='Sd_Nombre' />
            <RequiredRule />
          </Column>
          <Column caption='DESCRIPCIÓN' allowEditing={true} dataField='Az_Descripcion' visible={false} />
          <Column caption='ID UNICO' allowEditing={true} dataField='Az_IdentificadorUnico' alignment='center'>
            <RequiredRule />
          </Column>
          <Column caption='HOST QUERY' allowEditing={true} dataField='Az_ManualHQ' alignment='center' width={90} />
          <Column caption='ESTADO' allowEditing={true} dataField='Az_Estado' alignment='center' width={90} />
          <Column type='buttons' caption={'ACCIÓN'} width={150}>
            <Butt name='edit' />
            <Butt name='delete' />
            <Butt hint='Configurar' icon='eyeopen' visible={true} disabled={false} onClick={handleDetails} />
          </Column>
        </DataGrid>

        {/* Renderiza el modal solo cuando isModalVisible es true y los datos están listos */}

        <ModalAnalytesByAnalyzer visible={isModalVisible} setVisible={closeModal} idAz={selectedAnalizer.id} Analyzer={selectedAnalizer.name} Analitos={analitos} />
      </div>
    </div>
  );
};

export default Index;
 